import React, { useState } from "react";
import { AlertTriangle, Siren, X, CheckCircle2 } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";

/**
 * Big red SOS button with confirm dialog.
 * Variants:
 *   "fab"      — floating round button for owner app
 *   "headunit" — big rectangular button for car head-unit dashboard
 */
export default function SOSButton({ deviceId, variant = "fab" }) {
  const { t, formatApiError } = useAuth();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const trigger = async () => {
    setSending(true); setErr("");
    try {
      const { data } = await api.post("/sos", { device_id: deviceId });
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setSending(false); }
  };

  const close = () => {
    setOpen(false); setResult(null); setErr("");
  };

  const button =
    variant === "headunit" ? (
      <button
        data-testid="sos-btn-headunit"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-black tracking-wider shadow-lg active:scale-95"
      >
        <Siren size={20} /> {t.sos}
      </button>
    ) : (
      <button
        data-testid="sos-btn-fab"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-black tracking-wider shadow-lg active:scale-95 border-2 border-white"
        title={t.sosTrigger}
      >
        <Siren size={16} /> {t.sos}
      </button>
    );

  return (
    <>
      {button}
      {open && (
        <div
          data-testid="sos-dialog"
          className="fixed inset-0 z-[9999] flex items-center justify-center animate-fadeIn"
          style={{
            backgroundColor: "rgba(0,0,0,0.7)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
          onClick={close}
        >
          <div
            className="w-full max-w-[380px] bg-white rounded-2xl p-5 shadow-2xl mx-4"
            style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom))" }}
            onClick={(e) => e.stopPropagation()}
          >
            {!result && !err && (
              <>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-11 h-11 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
                      <AlertTriangle size={22} />
                    </div>
                    <div>
                      <h2 className="font-black text-lg text-slate-900">{t.sosConfirmTitle}</h2>
                      <p className="text-xs text-slate-500 max-w-sm mt-0.5">{t.sosConfirmBody}</p>
                    </div>
                  </div>
                  <button data-testid="sos-cancel" onClick={close} className="text-slate-400 p-1"><X size={18}/></button>
                </div>
                <div className="mt-6 flex gap-2">
                  <button
                    data-testid="sos-cancel-btn"
                    onClick={close}
                    className="flex-1 py-3 rounded-xl border border-slate-200 font-bold text-slate-600"
                  >
                    {t.cancel}
                  </button>
                  <button
                    data-testid="sos-confirm"
                    onClick={trigger}
                    disabled={sending}
                    className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-black active:scale-[0.98] disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                  >
                    <Siren size={16} /> {sending ? t.sosSending : t.sos}
                  </button>
                </div>
              </>
            )}

            {err && (
              <div data-testid="sos-error" className="text-center py-4">
                <AlertTriangle size={36} className="text-alert mx-auto mb-2" />
                <div className="font-bold text-slate-900">{err}</div>
                <button onClick={close} className="mt-4 px-4 py-2 rounded-xl bg-slate-100 font-semibold text-sm">
                  {t.cancel}
                </button>
              </div>
            )}

            {result && (
              <div data-testid="sos-success" className="text-center py-3">
                <CheckCircle2 size={44} className="text-moving mx-auto mb-2" />
                <div className="font-black text-xl text-slate-900">
                  {t.sosSent.replace("{n}", result.contacts_notified)}
                </div>
                <div className="text-xs text-slate-500 mt-1 font-semibold">{t.dryRun}</div>
                <div className="mt-3 text-left bg-slate-50 rounded-xl p-3 font-mono text-xs text-slate-700 break-all">
                  {result.share_url}
                </div>
                <button
                  data-testid="sos-done"
                  onClick={close}
                  className="mt-5 w-full py-3 rounded-xl bg-primary text-white font-bold"
                >
                  OK
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
